import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, Save, ChevronLeft, ChevronRight, Users, Car, Phone, TrendingUp } from 'lucide-react';
import { API } from '@/utils/api';
import axios from 'axios';
import { toast } from 'sonner';

const MONTHS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const ObjectivesManager = () => {
  const [employees, setEmployees] = useState([]);
  const [objectives, setObjectives] = useState({});
  const [overview, setOverview] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [empRes, objRes, overRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/dashboard/objectives?month=${month}&year=${year}`),
        axios.get(`${API}/dashboard/admin-overview?month=${month}&year=${year}`),
      ]);
      const nonAtelier = empRes.data.filter(e => ['admin', 'reception', 'vente', 'reception_vente', 'rh'].includes(e.role) && e.is_active);
      setEmployees(nonAtelier);
      setOverview(overRes.data);

      const objMap = {};
      objRes.data.forEach(o => { objMap[o.employee_id] = o; });
      nonAtelier.forEach(e => {
        if (!objMap[e.id]) {
          objMap[e.id] = { employee_id: e.id, vehicle_sales_target: 0, new_contacts_target: 0, follow_ups_target: 0 };
        }
      });
      setObjectives(objMap);
    } catch (e) { console.error(e); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const setTarget = (empId, field, value) => {
    setObjectives(prev => ({
      ...prev,
      [empId]: { ...prev[empId], [field]: parseInt(value) || 0 }
    }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const emp of employees) {
        const obj = objectives[emp.id];
        if (obj) {
          await axios.post(`${API}/dashboard/objectives`, {
            employee_id: emp.id,
            month, year,
            vehicle_sales_target: obj.vehicle_sales_target || 0,
            new_contacts_target: obj.new_contacts_target || 0,
            follow_ups_target: obj.follow_ups_target || 0,
          });
        }
      }
      toast.success('Objectifs enregistres');
      load();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  return (
    <div className="space-y-6" data-testid="objectives-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-bold text-slate-900">Objectifs mensuels</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium min-w-[120px] text-center">{MONTHS[month - 1]} {year}</span>
          <Button variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Performance Overview */}
      {overview && (
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-emerald-600">{overview.global_stats.total_sales}</div>
            <div className="text-xs text-slate-500">Ventes totales</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{overview.global_stats.total_new_clients}</div>
            <div className="text-xs text-slate-500">Nouveaux contacts</div>
          </Card>
        </div>
      )}

      {/* Objectives per employee */}
      <div className="space-y-4">
        {employees.map(emp => {
          const obj = objectives[emp.id] || {};
          const perf = overview?.employees?.find(e => e.employee_id === emp.id);
          const roleLabel = { admin: 'Admin', reception: 'Reception', vente: 'Vente', reception_vente: 'Rec+Vente', rh: 'RH' }[emp.role] || emp.role;

          return (
            <Card key={emp.id} className="p-4" data-testid={`obj-${emp.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-semibold text-sm text-slate-900">{emp.first_name} {emp.last_name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  emp.role === 'vente' ? 'bg-emerald-100 text-emerald-700' :
                  emp.role === 'reception' ? 'bg-sky-100 text-sky-700' :
                  emp.role === 'reception_vente' ? 'bg-violet-100 text-violet-700' :
                  'bg-red-100 text-red-700'
                }`}>{roleLabel}</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Car className="w-3 h-3" /> Ventes
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} className="h-8 text-sm w-20"
                      value={obj.vehicle_sales_target || ''}
                      onChange={e => setTarget(emp.id, 'vehicle_sales_target', e.target.value)}
                      data-testid={`obj-sales-${emp.id}`} />
                    {perf && <span className="text-xs font-bold text-emerald-600">{perf.vehicle_sales_actual} fait</span>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Contacts
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} className="h-8 text-sm w-20"
                      value={obj.new_contacts_target || ''}
                      onChange={e => setTarget(emp.id, 'new_contacts_target', e.target.value)}
                      data-testid={`obj-contacts-${emp.id}`} />
                    {perf && <span className="text-xs font-bold text-blue-600">{perf.new_contacts_actual} fait</span>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Relances
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} className="h-8 text-sm w-20"
                      value={obj.follow_ups_target || ''}
                      onChange={e => setTarget(emp.id, 'follow_ups_target', e.target.value)}
                      data-testid={`obj-followups-${emp.id}`} />
                    {perf && <span className="text-xs font-bold text-violet-600">{perf.follow_ups_actual} fait</span>}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Button className="w-full" onClick={saveAll} disabled={saving} data-testid="save-objectives-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Enregistrement...' : 'Enregistrer tous les objectifs'}
      </Button>
    </div>
  );
};

export default ObjectivesManager;
